import { FC, useMemo } from 'react';

import { Box, Header, Pressable } from '@rbilabs/universal-components';
import { isBefore, parseISO } from 'date-fns';
import { useIntl } from 'react-intl';

import Picture from 'components/picture';
import { useNavigation } from 'hooks/navigation/use-navigation';
import useEffectOnce from 'hooks/use-effect-once';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { isActiveWalmartUser } from 'state/loyalty/hooks/utils/walmart';
import { ClickEventComponentNames, CustomEventNames, useMParticleContext } from 'state/mParticle';
import { portalToFooter } from 'state/mobile-footer';
import { portalToReplaceHeader } from 'state/mobile-header-nav';
import { isIOS } from 'utils/environment';
import { routes } from 'utils/routing';

import { useLoyaltyWalmart } from '../walmart/use-loyalty-walmart';

import { LogoContainer, StyledBackIcon, StyledBox } from './styled';
import { WalmartDistributionOfferItem } from './walmart-distribution-offer-item';
import { WalmartOfferItem } from './walmart-offer-item';

export const WalmartOffer: FC<React.PropsWithChildren<{}>> = () => {
  const enableWalmartPlusIntegration = useFlag(LaunchDarklyFlag.ENABLE_WALMART_PLUS_TRIAL_OFFER);
  const { logButtonClick } = useMParticleContext();
  const { formatMessage } = useIntl();
  const { closeModalAuth, setAccountCallbackUrl } = useAuthContext();
  const { navigate } = useNavigation();
  const { loyaltyUser, loading: isLoadingLoyaltyUser } = useLoyaltyUser();
  const isWalmartUser = isActiveWalmartUser(loyaltyUser);
  const { data, loading } = useLoyaltyWalmart();

  const showDistributionOfferCard = useMemo(() => {
    // The Distribution Offer module is only shown to users who were linked
    // before the distributionOfferDate
    const distributionOfferDateStr = data?.partnershipPage?.distributionOfferDate;
    const userJoinedAtStr = loyaltyUser?.partnerUserData?.find(
      data => data.partner === 'WALMART' && data.status === 'ACTIVE'
    )?.createdAt;

    if (distributionOfferDateStr && userJoinedAtStr) {
      const distributionOfferDateDate = parseISO(distributionOfferDateStr);
      const userJoinedAtDate = parseISO(userJoinedAtStr);
      return isBefore(userJoinedAtDate, distributionOfferDateDate);
    }
    return false;
  }, [data?.partnershipPage?.distributionOfferDate, loyaltyUser?.partnerUserData]);

  const handleActivateAccount = () => {
    logButtonClick({
      attributes: {
        name: CustomEventNames.ACTIVATE_WALMART_PLUS_ACCOUNT,
        component: ClickEventComponentNames.BUTTON,
      },
    });
    navigate(routes.walmart);
  };

  useEffectOnce(() => {
    return () => {
      // TODO: Consider closing the modal using state in the useAuth context on mount.
      closeModalAuth();
    };
  });

  const handleClickWalmartOfferItemCard = () => {
    if (!isWalmartUser) {
      return;
    }

    navigate(enableWalmartPlusIntegration ? routes.offers : routes.walmart);
  };

  const WalmartLogo = () => (
    <LogoContainer>
      <Picture
        width="$24"
        image={data?.logo?.locale}
        alt={formatMessage({ id: 'deliveryLogoAlt' })}
      />
    </LogoContainer>
  );

  const HiddenAppHeader = portalToReplaceHeader(() => {
    return (
      <Box marginTop={isIOS() ? '$12' : '$0'}>
        <Pressable
          onPress={() => {
            setAccountCallbackUrl(routes.base);
            navigate(routes.account);
          }}
          padding="$4"
        >
          <StyledBackIcon variant="back" />
        </Pressable>
        <Header alignSelf="center" variant="headerTwo" top="$4" position="absolute">
          {formatMessage({ id: 'partnerOffers' })}
        </Header>
      </Box>
    );
  });
  const HiddenAppFooter = portalToFooter(() => null);

  return (
    (!loading && !isLoadingLoyaltyUser && (
      <>
        <StyledBox>
          <Pressable onPress={handleClickWalmartOfferItemCard}>
            <WalmartOfferItem
              name={formatMessage({ id: 'partnershipWalmartTitle' })}
              description={formatMessage({ id: 'partnershipWalmartDescription' })}
              descriptionL2={
                enableWalmartPlusIntegration && !isWalmartUser
                  ? formatMessage({ id: 'walmartPlusMembershipDiscount' })
                  : undefined
              }
              image={<WalmartLogo />}
              activateButtonLabel={formatMessage({ id: 'activate' })}
              linkedButtonLabel={formatMessage({ id: 'active' })}
              onPartnershipActivate={handleActivateAccount}
              isLoadingUser={isLoadingLoyaltyUser}
              isWalmartUser={isWalmartUser}
            />
          </Pressable>
        </StyledBox>

        {enableWalmartPlusIntegration && isWalmartUser && showDistributionOfferCard && (
          <StyledBox>
            <Pressable>
              <WalmartDistributionOfferItem
                name={formatMessage({ id: 'royalPerksMembers' })}
                description={formatMessage({ id: 'royalPerksMembershipDiscount' })}
                image={<WalmartLogo />}
                onConfirm={() => navigate(routes.walmart)}
                confirmButtonLabel={formatMessage({ id: 'learnMore' })}
              />
            </Pressable>
          </StyledBox>
        )}
        <HiddenAppHeader />
        <HiddenAppFooter />
      </>
    )) ||
    null
  );
};
